"use client";

import { useState } from "react";
import { History, SquarePen, TriangleAlert } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { ContextUsage } from "@/types/command-agent";
import { contextUsagePercent } from "@/utils/command-context-usage";

interface CommandContextUsageRingProps {
    usage?: ContextUsage | null;
    className?: string;
}

const AUTO_COMPACT_AT = 0.85;
const WARN_FROM = 75;
const EMPTY_USAGE: ContextUsage = { usedTokens: 0, contextWindow: 200_000 };

function ringTone(percent: number): string {
    if (percent >= 90) return "text-destructive";
    if (percent >= WARN_FROM) return "text-amber-500";
    return "text-primary";
}

function barTone(percent: number): string {
    if (percent >= 90) return "bg-destructive";
    if (percent >= WARN_FROM) return "bg-amber-500";
    return "bg-primary";
}

function usageState(usage: ContextUsage): "warn" | "compacted" | "ok" {
    if (contextUsagePercent(usage) >= WARN_FROM) return "warn";
    return usage.compacted ? "compacted" : "ok";
}

const STATE_COPY = {
    warn: "The context window is nearly full. Your chat will auto compact soon. Start a new one to keep responses accurate.",
    compacted: "Your session has auto compacted. Start a new chat for best results.",
    ok: "This shows how much of this chat the agent can keep in mind. Above 85%, your session will auto summarise. Start a new chat for best results.",
};

export function formatContextUsagePercent(usage?: ContextUsage | null): string {
    if (!usage) return "0";
    const percent = contextUsagePercent(usage);
    return percent < 1 && percent > 0 ? percent.toFixed(1) : Math.round(percent).toString();
}

export function CommandContextUsageRing({ usage, className }: CommandContextUsageRingProps) {
    const percent = usage ? contextUsagePercent(usage) : 0;
    const radius = 9;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference * (1 - percent / 100);

    return (
        <span
            className={cn("relative inline-flex size-4 shrink-0 items-center justify-center", className)}
            aria-hidden
        >
            <svg viewBox="0 0 24 24" className={cn("size-4 -rotate-90", ringTone(percent))}>
                <circle
                    cx="12"
                    cy="12"
                    r={radius}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    className="opacity-20"
                />
                <circle
                    cx="12"
                    cy="12"
                    r={radius}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    className="transition-[stroke-dashoffset] duration-200 ease-linear"
                />
            </svg>
        </span>
    );
}

export function CommandContextUsagePill({
    usage,
    onNewChat,
}: {
    usage?: ContextUsage | null;
    onNewChat?: () => void;
}) {
    const resolved = usage ?? EMPTY_USAGE;
    const [panelOpen, setPanelOpen] = useState(false);
    const [tipOpen, setTipOpen] = useState(false);
    const percent = contextUsagePercent(resolved);
    const displayPercent = formatContextUsagePercent(resolved);
    const state = usageState(resolved);

    return (
        <Popover open={panelOpen} onOpenChange={setPanelOpen}>
            <Tooltip open={tipOpen && !panelOpen} onOpenChange={setTipOpen}>
                <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            aria-label={`Context used ${displayPercent} percent`}
                            aria-expanded={panelOpen}
                            className={cn(
                                "inline-flex h-7 shrink-0 items-center gap-1 rounded-full border border-border px-2 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary",
                                panelOpen && "border-primary/40 text-primary"
                            )}
                        >
                            <CommandContextUsageRing usage={resolved} />
                            <span className="tabular-nums">{displayPercent}%</span>
                        </button>
                    </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[260px] space-y-1 text-xs sm:max-w-[260px]">
                    <p className="font-medium">{displayPercent}% of context used</p>
                    <p className={cn(state === "warn" && "text-amber-600 dark:text-amber-400")}>
                        {STATE_COPY[state]}
                    </p>
                </TooltipContent>
            </Tooltip>

            <PopoverContent align="end" side="top" className="w-[300px] space-y-3 p-4">
                <div className="flex items-baseline justify-between gap-3">
                    <p className="text-sm font-medium text-foreground">Context window</p>
                    <p className="text-xs tabular-nums text-muted-foreground">{displayPercent}% used</p>
                </div>

                <div className="relative h-2 rounded-full bg-muted">
                    <div
                        className={cn("h-full rounded-full transition-[width] duration-200", barTone(percent))}
                        style={{ width: `${Math.max(percent, percent > 0 ? 2 : 0)}%` }}
                    />
                    <span
                        className="absolute -top-0.5 h-3 w-px bg-foreground/30"
                        style={{ left: `${AUTO_COMPACT_AT * 100}%` }}
                        aria-hidden
                    />
                </div>

                <div
                    className={cn(
                        "flex gap-2 rounded-lg px-3 py-2 text-xs leading-relaxed",
                        state === "warn"
                            ? "bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300"
                            : "bg-muted/60 text-muted-foreground"
                    )}
                >
                    {state === "warn" ? (
                        <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
                    ) : state === "compacted" ? (
                        <History className="mt-0.5 size-3.5 shrink-0" />
                    ) : null}
                    <p>{STATE_COPY[state]}</p>
                </div>

                {onNewChat && resolved.usedTokens > 0 ? (
                    <button
                        type="button"
                        onClick={() => {
                            setPanelOpen(false);
                            onNewChat();
                        }}
                        className="flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-border text-xs font-medium text-foreground transition-colors hover:bg-muted"
                    >
                        <SquarePen className="size-3.5" />
                        New chat
                    </button>
                ) : null}
            </PopoverContent>
        </Popover>
    );
}
